import { useState } from 'react';
import { X, CreditCard, Loader, Lock } from 'lucide-react';
import { CardElement, useStripe, useElements } from '@stripe/react-stripe-js';

const AddStripeCardModal = ({ isOpen, onClose, onSubmit, clientSecret }) => {
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');

  const stripe = useStripe();
  const elements = useElements();

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !clientSecret) return;
    setProcessing(true);
    setError('');
    try {
      const card = elements.getElement(CardElement);
      if (!card) {
        setError('Card input is not ready yet.');
        setProcessing(false);
        return;
      }

      // SetupIntent confirmation using CardElement (no PaymentElement).
      // redirect: 'if_required' tells Stripe.js only to redirect if the payment method requires it (3DS/SCA).
      const { error: confirmError, setupIntent } = await stripe.confirmCardSetup(
        clientSecret,
        {
          payment_method: {
            card,
          },
        },
        { redirect: 'if_required' }
      );

      if (confirmError) {
        setError(confirmError.message || 'Setup failed');
        setProcessing(false);
        return;
      }

      if (setupIntent?.payment_method) {
        await onSubmit(setupIntent.payment_method);
        onClose();
      }
    } catch (err) {
      console.error('Setup error:', err);
      setError(err.message || 'Something went wrong');
    } finally {
      setProcessing(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="relative bg-white rounded-2xl shadow-xl w-full max-w-xl p-6" onClick={e => e.stopPropagation()}>
          <div className="flex justify-between items-center mb-6">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 bg-indigo-100 rounded-xl flex items-center justify-center">
                <CreditCard className="h-5 w-5 text-indigo-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-slate-900">Add Card</h3>
                <p className="text-xs text-slate-500">Securely save a card for bookings</p>
              </div>
            </div>
            <button type="button" onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 rounded-lg">
              <X className="h-5 w-5" />
            </button>
          </div>

          {!clientSecret ? (
            <div className="flex items-center justify-center py-8">
              <Loader className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <div className="mb-4">
                <label className="block text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">
                  Card Details
                </label>

                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <CardElement
                    options={{
                      hidePostalCode: true,
                      style: {
                        base: {
                          fontSize: '14px',
                          color: '#0f172a',
                          '::placeholder': { color: '#94a3b8' },
                        },
                        invalid: { color: '#dc2626' },
                      },
                    }}
                    onChange={(event) => {
                      if (event.error) setError(event.error.message);
                      else setError('');
                    }}
                  />
                </div>
              </div>

              {error && (
                <div className="mb-4 p-3 bg-red-50 border border-red-100 rounded-xl">
                  <p className="text-sm text-red-600">{error}</p>
                </div>
              )}

              <div className="flex items-center gap-2 text-xs text-slate-500 mb-6">
                <Lock className="h-4 w-4" />
                <span>Secured by Stripe</span>
              </div>

              <div className="flex gap-3">
                <button 
                  type="button" 
                  onClick={onClose} 
                  className="flex-1 py-3 border border-slate-200 rounded-xl text-slate-700 font-medium hover:bg-slate-50 transition-colors"
                >
                  Cancel
                </button>
                <button 
                  type="submit" 
                  disabled={processing || !stripe} 
                  className="flex-1 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium disabled:opacity-50 flex items-center justify-center gap-2 transition-all shadow-sm"
                >
                  {processing ? <Loader className="h-4 w-4 animate-spin" /> : null}
                  {processing ? 'Processing...' : 'Save Card'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default AddStripeCardModal;
